import React, { useEffect, useState, useContext } from 'react';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
import { useForm } from 'react-hook-form';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import RHFCheckbox from 'src/components/controls/tempRHFControls/RHFCheckBox';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import FormRow from 'src/components/controls/forms/FormRow';
import { ODataOptions, ODataFilter, ODataFilterOperators, ODataOrderByDirection } from 'src/util/ODataBuilder';
import { AppContext } from 'src/providers/AppContextProvider';
import { IAppContextState } from 'src/reducers/AppContext';
import { ITableConfig } from 'src/components/common/table';
import useFetch from 'use-http';
import _ from 'lodash';
import ProspectDetails from 'src/models/projectSettings/TsdiProspect';
import TsdiClient from 'src/models/AP/Client';

interface IFindProjectDialogProps {
    dialogName: string;
    hideDialog: () => void;
    onCancel?: () => void;
    onAddNewProject?: (payload: ProspectDetails) => void;
    resetProjectSearchOptions?: () => void;
    setProjectSearchTerm?: (keyword: string) => void;
    incrementProjects?: () => void;
    projects?: ProspectDetails[];
    setSelectedProject?: (project: ProspectDetails) => void;
    setSelectedClient?: (project: TsdiClient) => void;
    style?: React.CSSProperties;
    addNewProject?: boolean;
    defaultProject?: number;
    mileageRate?: number;
    dayRate?: number;
    clientNumber?: number;
    clientTypes?: { code: number, description: string}[];
    billByOptions?: Array<{ value: number, label: string}>;
    offices?: Array<{ value: number, label: string }>;
    managers?: Array<{ value: number, label: string }>;
}

const FindProjectDialog = (props: IFindProjectDialogProps) => {
    const appContext = useContext<IAppContextState>(AppContext);
    const { register, errors, getValues } = useForm();
    const [fetchNextProjectsPage, setFetchNextProjectsPage] = useState<boolean>(true);
    const [projectSearchOptions, setProjectSearchOptions] = useState<ODataOptions>(undefined);
    const [projectSearchTerm, setProjectSearchTerm] = useState<string>('');
    const [projects, setProjects] = useState<Array<ProspectDetails>>(undefined);
    const [selectedProject, setSelectedProject] = useState<number>(null);
    const { get, response } = useFetch();
    const paged: boolean = false;
    const billingMethodOptions = [{ value: '0', label: 'Land' }];
    // const dispatch = useDispatch();

    useEffect(() => {
        onCreate();
    }, []);

    useEffect(() => {
        if (!!projectSearchOptions) {
            getProjects();
        }
    }, [projectSearchOptions]);

    useEffect(() => {
        if ((!!projectSearchTerm || projectSearchTerm === '') && !!projectSearchOptions) {
            resetProjectSearchOptions();
        }
    }, [projectSearchTerm]);

    useEffect(() => {
        if (!!props.clientNumber) {
            getProjects();
        }
    }, [props.clientNumber]);

    const onCreate = () => {        
        initializeClientSearchSettings();
    };

    const resetState = () => {
        setSelectedProject(!!props?.defaultProject ? props?.defaultProject : 1);
        setProjectSearchTerm('');
        resetProjectSearchOptions();
        setProjects([]);
    };

    const OKClicked = () => {
        const formData = getValues();
        props.setSelectedProject(projects[selectedProject]);
        if (!!formData?.searchClientNumber) {
            props.setSelectedClient(formData.searchClientNumber);
        }
        props.hideDialog();
        resetState();
    };

    const onProjectSearchChanged = (searchContent: string) => {
        search();
    };

    const onCancel = () => {
        props.hideDialog();        
        resetState();
    };

    const initializeClientSearchSettings = () => {
        const tableConfig: ITableConfig = {
            rowCount: 1000,
            idField: 'PR_ID',
            pageNumber: 1,
            columns: []
        };
        setProjectSearchOptions(ODataOptions.from(tableConfig).addOrderBy('PR_NO', ODataOrderByDirection.Ascending));
    };

    const getScrollStatus = (divElement: HTMLDivElement) => {
        if (divElement && !!paged) {
            const scrollTop = divElement.scrollTop % 1 > .9 ? divElement.scrollTop : Math.round(divElement.scrollTop);
            const scrollHeight = divElement.scrollHeight;
            const clientHeight = divElement.clientHeight;

            // Check if reached the end of the list
            if (scrollTop + clientHeight >= scrollHeight) {
                incrementProjects();
                console.log('end of scroll YES');
            } else {
                console.log('end of scroll NO');
            }
        }
    };

    const incrementProjects = () => {
        if (!!fetchNextProjectsPage) {
            let newSearchOptions = _.cloneDeep(projectSearchOptions);
            newSearchOptions.incrementPageNumber();
            setProjectSearchOptions(newSearchOptions);
        }
    };

    const selectProjectAndClose = (project: ProspectDetails) => {
        props.setSelectedProject(project);
        props.hideDialog();
        resetState();
    };

    const resetProjectSearchOptions = () => {
        setFetchNextProjectsPage(true);
        console.log(fetchNextProjectsPage);
        let newProjectSearchOptions = _.cloneDeep(projectSearchOptions);
        newProjectSearchOptions.clearFilters();
        newProjectSearchOptions.addPaging(1, 1000);
        if (typeof projectSearchTerm === 'string') {
            const fieldValues = projectSearchTerm.split(',');
            if (fieldValues[0] !== undefined && !isNaN(Number(fieldValues[0])) && fieldValues[0] !== ' ' && fieldValues[0]?.length > 0) {
                newProjectSearchOptions.addFilter(new ODataFilter('p.[CL_NO]', ODataFilterOperators.Equals, !!projectSearchTerm ? fieldValues[0] : ''));
            }
            if (fieldValues[1] !== undefined && !isNaN(Number(fieldValues[1])) && fieldValues[1] !== ' ') {
                newProjectSearchOptions.addFilter(new ODataFilter('p.[PR_NO]', ODataFilterOperators.Equals, !!projectSearchTerm ? fieldValues[1] : ''));
            }
            if (fieldValues[2] !== undefined && fieldValues[2]?.length > 0 && fieldValues[2] !== ' ') {
                newProjectSearchOptions.addFilter(new ODataFilter('p.[PR_NAME]', ODataFilterOperators.Contains, !!projectSearchTerm ? fieldValues[2] : ''));
            }
            if (fieldValues[3] !== undefined && !isNaN(Number(fieldValues[3])) && fieldValues[3] !== ' ') {
                newProjectSearchOptions.addFilter(new ODataFilter('p.[TSD_MGR]', ODataFilterOperators.Contains, !!projectSearchTerm ? fieldValues[3] : ''));
            }
            if (fieldValues[4] !== undefined && !isNaN(Number(fieldValues[4])) && fieldValues[4] !== ' ') {
                newProjectSearchOptions.addFilter(new ODataFilter('p.[PROJECT_COORDINATOR]', ODataFilterOperators.Contains, !!projectSearchTerm ? fieldValues[4] : ''));
            }
            if (fieldValues[5] !== undefined && fieldValues[5]?.length > 0 && fieldValues[5] !== ' ') {
                newProjectSearchOptions.addFilter(new ODataFilter('p.[OFFICE]', ODataFilterOperators.Contains, !!projectSearchTerm ? fieldValues[5] : ''));
            }
        }
        setProjectSearchOptions(newProjectSearchOptions);
    };

    const getProjects = async () => {
        const queryString = projectSearchOptions?.toString();
        let result = await get(`/v1.0/ClientProjectMaintenance/projects/byclient/${props?.clientNumber}/${queryString}`);

        if (response.ok) {
            setProjects(result);
        } else {
            setProjects([]);
            console.log('Unable to get client data!');
        }
    };

    const search = _.debounce(() => {
        let searchString =  (!!getValues()?.searchClientNumber ? getValues().searchClientNumber : ' ');
        searchString += ',' +  (!!getValues()?.searchProjectNumber ? getValues().searchProjectNumber : ' ');
        searchString += ',' +  (!!getValues()?.searchProjectName ? getValues().searchProjectName : ' ');
        searchString += ',' + (!!getValues()?.managerNumber ? getValues().managerNumber : ' ');
        searchString += ',' + (!!getValues()?.pcapmNumber ? getValues().pcapmNumber : ' ');
        searchString += ',' + (!!getValues()?.originatingOffice ? getValues().originatingOffice : ' ');
        setProjectSearchTerm(searchString);
    }, 700);

    const onCreateProject = () => {
        const payload = getValues();
        const transformedPayload = new ProspectDetails();
        transformedPayload.clientNumber = !!props?.clientNumber ? props?.clientNumber : 0;
        transformedPayload.name = payload.searchProjectName;
        transformedPayload.backupOfficeRate = !isNaN(Number(payload.backupOfficeRate)) ? Number(payload.backupOfficeRate) : 250.00;
        transformedPayload.mileageRate = !isNaN(Number(payload.mileageRate)) ? Number(payload.mileageRate) : .7;
        transformedPayload.type = 'L';
        transformedPayload.dayRate = props?.dayRate;
        transformedPayload.billMode = 0;
        transformedPayload.billBy = payload.billingbyMethod;
        transformedPayload.bopRate = payload.bopRate = 0;
        transformedPayload.turnkey = payload.turnkeyBilling;
        transformedPayload.county = payload.searchProjectCountry;
        transformedPayload.state = payload.searchProjectState;
        transformedPayload.modByNo = Number(appContext.currentUser.userId);
        transformedPayload.modByName = appContext.currentUser.username;
        transformedPayload.managingOffice = props?.offices ? props.offices.find(o => o.value === Number(payload.originatingOffice))?.label : 'OK';
        transformedPayload.manager = payload.projectManager;
        props.onAddNewProject(transformedPayload);
    };

    return (
        <>
        <Dialog title={!!props.addNewProject ? 'Add Project' : 'Find Project'} dialogName={props.dialogName} isExitButtonInvisible={true} bodyStyle={{ maxHeight: '75vh', width: '40%' }}>
        <form>
            <div style={{display: 'inline-block', marginTop: '1rem'}}>
                <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'flex-start', alignItems: 'flex-end'}}>
                    <RHFInputBox
                        onChange={(event) => onProjectSearchChanged(event.currentTarget.value)}
                        labelOnLeft={true}
                        labelStyle={{whiteSpace: 'nowrap'}}
                        label="Client #"
                        id={'searchClientNumber'}
                        name={'searchClientNumber'}
                        style={{whiteSpace: 'nowrap', width: '80%'}}
                        bodyStyle={{whiteSpace: 'nowrap', width: '20%'}}
                        register={register}
                        disabled={!!props.addNewProject ? true : false}
                        defaultVal={!!props?.clientNumber ? props?.clientNumber?.toString() : ''}
                    />
                    <RHFInputBox
                        onChange={(event) => onProjectSearchChanged(event.currentTarget.value)}
                        labelOnLeft={true}
                        label="Project #"
                        name={'searchProjectNumber'}
                        style={{whiteSpace: 'nowrap', display: 'flex', alignItems: 'baseline', width: '80%'}}
                        bodyStyle={{whiteSpace: 'nowrap', display: 'flex', alignItems: 'baseline', width: '20%'}}
                        register={register}
                        disabled={props?.addNewProject}
                        defaultVal={props?.addNewProject ? ((Array.isArray(projects) ? projects.length : 0) + 1).toString() : null}
                    />
                    {!!props.addNewProject && billingMethodOptions.length > 1 && <RHFSelect 
                        label="Billing Method"
                        name={`billingFrequency`}
                        options={billingMethodOptions}
                        register={register} 
                        errors={errors}
                        style={{whiteSpace: 'nowrap', display: 'flex', alignItems: 'baseline', width: '75%'}}
                        bodyStyle={{whiteSpace: 'nowrap', display: 'flex', alignItems: 'baseline', width: '100%'}}
                    />}
                    {props?.addNewProject && billingMethodOptions.length < 2 && <label style={{marginBottom: '.6rem', whiteSpace: 'nowrap'}}>Billing Method: Land</label>}
                </div>
                <FormRow>
                <RHFInputBox
                    onChange={(event) => onProjectSearchChanged(event.currentTarget.value)}
                    labelOnLeft={true}
                    label="Name"
                    name={'searchProjectName'}
                    style={{whiteSpace: 'nowrap', display: 'flex', alignItems: 'baseline', width: '100%'}}
                    bodyStyle={{whiteSpace: 'nowrap', display: 'flex', alignItems: 'baseline', width: '30%'}}
                    register={register}
                />
                {!!props.addNewProject && <RHFInputBox
                    labelOnLeft={true}
                    label="Mileage Rate $"
                    name={'mileageRate'}
                    defaultVal={props?.mileageRate?.toString()}
                    style={{width: '100%'}}
                    bodyStyle={{width: '15%'}}
                    register={register}
                />}
                {!!props.addNewProject && <RHFSelect 
                    label="Billing by Method"
                    labelOnLeft={true}
                    labelStyle={{whiteSpace: 'nowrap'}}
                    name={`billingbyMethod`}
                    options={props?.billByOptions ? props.billByOptions : [
                        { value: 'G', label: 'Day/Account' },
                        { value: 'Y', label: 'Day/Breakdown' },
                        { value: 'L', label: 'Day/Landman' },
                        { value: 'D', label: 'Day/Project' },
                        { value: 'E', label: 'Day/Subcode' },
                        { value: 'F', label: 'Day/Tract' },
                        { value: 'U', label: 'Day/AltSubcode' },
                        { value: 'K', label: 'Hour/Account' },
                        { value: 'Z', label: 'Hour/Breakdown' },
                        { value: 'M', label: 'Hour/Landman' },
                        { value: 'H', label: 'Hour/Project' },
                        { value: 'I', label: 'Hour/Subcode' },
                        { value: 'J', label: 'Hour/Tract' },
                        { value: 'V', label: 'Hour/AltSubcode' },
                    ]}
                    register={register} 
                    errors={errors}
                    style={{width: '100%' }}
                    bodyStyle={{whiteSpace: 'nowrap', width: '30%' }}
                />}
                </FormRow>
                <FormRow>
                {!!props.addNewProject && <RHFSelect 
                    label="Originating Office"
                    labelStyle={{whiteSpace: 'nowrap'}}
                    labelOnLeft={true}
                    name={`originatingOffice`}
                    options={props?.offices ?? [
                        { value: 0, label: 'OK' },
                        { value: 1, label: 'CO' },
                        { value: 2, label: 'IH' },
                        { value: 3, label: 'MD' },
                        { value: 4, label: 'PA' },
                        { value: 5, label: 'RN' },
                        { value: 6, label: 'TX' }
                    ]}
                    register={register} 
                    errors={errors}
                    style={{ width: '60%' }}
                    bodyStyle={{ whiteSpace: 'nowrap', width: '25%' }}
                />}
                {!!props.addNewProject && <RHFCheckbox
                    name="turnkeyBilling"
                    label="Turnkey Billing?"
                    register={register}
                    errors={errors}
                    bodyStyle={{width: '20%'}}
                />}
                </FormRow>
                <FormRow>
                    <RHFSelect
                        onChange={(event) => onProjectSearchChanged(event.currentTarget.value)}
                        label="Manager"
                        labelStyle={{whiteSpace: 'nowrap'}}
                        labelOnLeft={true}
                        name={`projectManager`}
                        options={props?.managers ?? []}
                        register={register} 
                        errors={errors}
                        style={{ width: '100%' }}
                        bodyStyle={{ whiteSpace: 'nowrap', width: '60%' }}
                    />
                <RHFInputBox
                    onChange={(event) => onProjectSearchChanged(event.currentTarget.value)}
                    label={'Manager'}
                    labelStyle={{whiteSpace: 'nowrap'}}
                    labelOnLeft={true}
                    name={'managerNumber'}
                    style={{ width: '30%' }}
                    bodyStyle={{width: '30%' }}
                    register={register}
                />
                {!!props.addNewProject && <RHFInputBox
                    labelStyle={{whiteSpace: 'nowrap'}}
                    name={'managerName'}
                    register={register}
                    style={{ width: '30%' }}
                    bodyStyle={{width: '100%' }}
                />}
                </FormRow>
                <FormRow>
                    <RHFInputBox
                        onChange={(event) => onProjectSearchChanged(event.currentTarget.value)}
                        label={'PC / APM '}
                        labelStyle={{whiteSpace: 'nowrap'}}
                        labelOnLeft={true}
                        name={'pcapmNumber'}
                        register={register}
                        style={{ width: '100%' }}
                        bodyStyle={{width: '20%' }}
                    />
                    {!!props.addNewProject && <RHFInputBox
                        name={'pcapName'}
                        register={register}
                        style={{ width: '100%' }}
                        bodyStyle={{width: '50%' }}
                    />}
                </FormRow>
                <FormRow>
                {!!props.addNewProject && <RHFInputBox
                    onChange={(event) => onProjectSearchChanged(event.currentTarget.value)}
                    labelOnLeft={true}
                    label="City"
                    name={'searchProjectCity'}
                    style={{ width: '100%' }}
                    bodyStyle={{width: '40%' }}
                    register={register}
                />}
                {!!props.addNewProject && <RHFInputBox
                    onChange={(event) => onProjectSearchChanged(event.currentTarget.value)}
                    labelOnLeft={true}
                    label="State"
                    name={'searchProjectState'}
                    style={{ width: '100%' }}
                    bodyStyle={{width: '30%' }}
                    register={register}
                />}
                {!!props.addNewProject && <RHFInputBox
                    onChange={(event) => onProjectSearchChanged(event.currentTarget.value)}
                    labelOnLeft={true}
                    label="Zip"
                    name={'searchClientZip'}
                    style={{width: '50px'}}
                    register={register}
                />}
                </FormRow>
                <div style={{display: 'flex'}}>
                    {!!props.addNewProject && <RHFInputBox
                        onChange={(event) => onProjectSearchChanged(event.currentTarget.value)}
                        labelOnLeft={true}
                        label="County"
                        name={'searchProjectCounty'}
                        style={{ width: '100%' }}
                        bodyStyle={{width: '40%' }}
                        register={register}
                    />}
                    {!!props.addNewProject && <RHFInputBox
                        onChange={(event) => onProjectSearchChanged(event.currentTarget.value)}
                        labelOnLeft={true}
                        label="Area Code"
                        name={'searchAreaCode'}  
                        style={{ width: '100%' }}
                        bodyStyle={{width: '20%' }}
                        register={register}
                    />}
                </div>
                {!props.addNewProject && <RHFInputBox
                    onChange={(event) => onProjectSearchChanged(event.currentTarget.value)}
                    labelOnLeft={true}
                    label="Prospect Name"
                    name={'SearchProspect'}
                    style={{width: '200px'}}
                    register={register}
                />}
            </div>
            <div style={{marginTop: '3rem', overflowY: 'hidden', maxHeight: '200px'}}>
                    {props.addNewProject && <div style={{fontWeight: 'bold', fontFamily: 'Arial', width: '100%', textAlign: 'center'}}>
                        Existing Projects
                    </div>}
                    <table>
                        <thead style={{ textAlign: 'left' }}>
                            <tr>
                                <th>Client #</th>
                                <th>#</th>
                                <th>Name</th>
                                <th>Type</th>
                                <th>Manager #</th>
                            </tr>
                        </thead>
                        <tbody onScroll={(event) => getScrollStatus(event.currentTarget)}>
                        {projects && projects?.map((project: ProspectDetails, index: number) => {
                            console.log(projects.length);
                            return (
                                    // tslint:disable-next-line
                                    <tr key={project.number + index} onDoubleClick={() => selectProjectAndClose(project)} onClick={() => setSelectedProject(index) } className="table-item-hover" style={{ textAlign: 'left' }}>
                                        <td style={{ width: '7%' }}>
                                            <RHFInputBox
                                                name={`clientNumber`}
                                                type="text"
                                                defaultVal={!!props?.clientNumber ? props?.clientNumber?.toString() : (!!project?.clientNumber ? project.clientNumber.toString() : '')}
                                                register={register}
                                                errors={errors}
                                                style={{ width: '100%' }}
                                                bodyStyle={{ width: '100%' }}
                                            />
                                        </td>
                                        <td style={{ width: '7%' }}>
                                            <RHFInputBox
                                                name={`newProjectNumber`}
                                                type="text"
                                                defaultVal={project?.number?.toString()}
                                                register={register}
                                                errors={errors}
                                                style={{ width: '100%' }}
                                                bodyStyle={{ width: '100%' }}
                                            />
                                        </td>
                                        <td style={{ width: '30%' }}>
                                            <RHFInputBox
                                                name={`newProjectName`}
                                                type="text"
                                                defaultVal={project?.name}
                                                register={register}
                                                errors={errors}
                                                style={{ width: '100%' }}
                                                bodyStyle={{ width: '100%' }}
                                            />
                                        </td>
                                        <td style={{ width: '30%' }}>
                                            <RHFInputBox
                                                name={`newProjectType`}
                                                type="text"
                                                defaultVal={project?.type}
                                                register={register}
                                                errors={errors}
                                                style={{ width: '100%' }}
                                                bodyStyle={{ width: '100%' }}
                                            />
                                        </td>
                                        <td style={{ width: '30%' }}>
                                            <RHFInputBox
                                                name={`newProjectManager`}
                                                type="text"
                                                defaultVal={project?.manager?.toString()}
                                                register={register}
                                                errors={errors}
                                                style={{ width: '100%' }}
                                                bodyStyle={{ width: '100%' }}
                                            />
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </form>
            <div className="form-row" style={{marginTop: '2rem', marginLeft: '33%', textAlign: 'center'}}>
                {!!props.addNewProject && <Button
                    className="blue-hover-button"
                    style={{whiteSpace: 'nowrap', width: '28%', marginRight: '1rem' }}
                    text={'Save New Project'}
                    onClick={() => onCreateProject()}
                />}
                {!props.addNewProject && <Button
                    className="blue-hover-button"
                    style={{ width: '24%', marginRight: '1rem' }}
                    text={'OK'}
                    onClick={() => OKClicked()}
                />}
                <Button
                    className="blue-hover-button"
                    style={{ width: '24%', marginRight: '1rem' }}
                    text={'Cancel'}
                    onClick={() => onCancel()}
                />
            </div>
        </Dialog>
        </>
    );
};

export default FindProjectDialog;



// WEBPACK FOOTER //
// ./src/components/client-project/dialogs/FindProjectDialog.tsx